import React, { useState, useCallback } from 'react';

const API_URL = (process.env.REACT_APP_API_URL_NODE || 'http://localhost:3002').replace(/\/$/, '');

/* =========================
   Helpers
========================= */
const fmt = (n) => n?.toLocaleString('it-IT') ?? '0';
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function Badge({ color, label, value }) {
    const colors = {
        green: { bg: '#e8f5e9', border: '#4CAF50', text: '#2e7d32' },
        blue: { bg: '#e3f2fd', border: '#2196F3', text: '#1565c0' },
        orange: { bg: '#fff3e0', border: '#FF9800', text: '#e65100' },
        purple: { bg: '#f3e5f5', border: '#9C27B0', text: '#6a1b9a' },
        grey: { bg: '#f5f5f5', border: '#9e9e9e', text: '#424242' },
    };
    const c = colors[color] || colors.grey;
    return (
        <div style={{ background: c.bg, border: `2px solid ${c.border}`, borderRadius: 10, padding: '14px 20px', textAlign: 'center', minWidth: 110 }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: c.text }}>{fmt(value)}</div>
            <div style={{ fontSize: 12, color: c.text, marginTop: 2 }}>{label}</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 28 }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: 15, color: '#555', borderBottom: '2px solid #eee', paddingBottom: 6 }}>{title}</h3>
            {children}
        </div>
    );
}

function Table({ cols, rows, emptyMsg = 'Nessun dato nel periodo selezionato' }) {
    if (!rows?.length) return <p style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>{emptyMsg}</p>;
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                        {cols.map(c => (
                            <th key={c.key} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{c.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #eee', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                            {cols.map(c => (
                                <td key={c.key} style={{ padding: '7px 10px', color: '#333' }}>
                                    {c.render ? c.render(row) : row[c.key]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* =========================
   Componente principale
========================= */
export default function ContactReport() {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    const [secret, setSecret] = useState('');
    const [from, setFrom] = useState(monthAgo);
    const [to, setTo] = useState(today);
    const [osmId, setOsmId] = useState('');
    const [eventId, setEventId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const fetchReport = useCallback(async () => {
        if (!secret) { setError('Inserisci la chiave admin'); return; }
        setLoading(true); setError(null); setData(null);
        try {
            const params = new URLSearchParams({ secret, from, to });
            if (osmId.trim()) params.set('osmId', osmId.trim());
            if (eventId.trim()) params.set('eventId', eventId.trim());

            const res = await fetch(`${API_URL}/api/admin/contact-report?${params}`);
            if (!res.ok) {
                const e = await res.json();
                throw new Error(e.error || `HTTP ${res.status}`);
            }
            setData(await res.json());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [secret, from, to, osmId, eventId]);

    // Raggruppa le righe POI per osmId mostrando tutti e tre i canali
    const poiMerged = data ? (() => {
        const map = {};
        const add = (rows, channel) => rows.forEach(r => {
            if (!map[r.osmId]) map[r.osmId] = { osmId: r.osmId };
            map[r.osmId][channel] = r.count;
            map[r.osmId][`${channel}Unique`] = r.uniqueCount;
            map[r.osmId][`${channel}Last`] = r.lastClick;
        });
        add(data.poi.whatsapp, 'wa');
        add(data.poi.phone, 'phone');
        add(data.poi.email, 'email');
        return Object.values(map).sort((a, b) =>
            ((b.wa || 0) + (b.phone || 0) + (b.email || 0)) - ((a.wa || 0) + (a.phone || 0) + (a.email || 0))
        );
    })() : [];

    // Raggruppa i click evento per eventId + type
    const eventMerged = data ? (() => {
        const map = {};
        data.events.forEach(r => {
            if (!map[r.eventId]) map[r.eventId] = { eventId: r.eventId };
            map[r.eventId][r.type] = r.count;
            map[r.eventId][`${r.type}Unique`] = r.uniqueCount;
        });
        return Object.values(map).sort((a, b) =>
            ((b.whatsapp || 0) + (b.phone || 0) + (b.email || 0)) - ((a.whatsapp || 0) + (a.phone || 0) + (a.email || 0))
        );
    })() : [];

    const inputStyle = {
        padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6,
        fontSize: 13, width: '100%', boxSizing: 'border-box'
    };

    return (
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', fontFamily: 'system-ui, sans-serif', color: '#333' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, borderBottom: '3px solid #4CAF50', paddingBottom: 14 }}>
                <span style={{ fontSize: 28 }}>📊</span>
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, color: '#2e7d32' }}>Report Contatti AITF</h1>
                    <p style={{ margin: '2px 0 0 0', fontSize: 13, color: '#888' }}>Quante persone hanno contattato attività e gestori tramite AI Hiking</p>
                </div>
            </div>

            {/* Filtri */}
            <div style={{ background: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>🔑 Chiave Admin</label>
                        <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
                            placeholder="admin secret" style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>📅 Dal</label>
                        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>📅 Al</label>
                        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>🗺️ Filtra OSM ID (opz.)</label>
                        <input value={osmId} onChange={e => setOsmId(e.target.value)}
                            placeholder="es. 1234567890" style={inputStyle} />
                    </div>
                    <div>
                        <label style={{ fontSize: 12, fontWeight: 600, color: '#666', display: 'block', marginBottom: 4 }}>✨ Filtra Evento ID (opz.)</label>
                        <input value={eventId} onChange={e => setEventId(e.target.value)}
                            placeholder="MongoDB _id" style={inputStyle} />
                    </div>
                </div>
                <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={fetchReport} disabled={loading}
                        style={{ padding: '9px 20px', background: '#4CAF50', color: 'white', border: 'none', borderRadius: 7, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
                        {loading ? '⏳ Caricamento...' : '🔍 Genera Report'}
                    </button>
                    {/* Shortcuts periodo */}
                    {[
                        { label: '7 giorni', days: 7 },
                        { label: '30 giorni', days: 30 },
                        { label: '90 giorni', days: 90 },
                    ].map(({ label, days }) => (
                        <button key={days} onClick={() => {
                            setFrom(new Date(Date.now() - days * 86400000).toISOString().slice(0, 10));
                            setTo(today);
                        }} style={{ padding: '9px 14px', background: 'white', color: '#555', border: '1px solid #ddd', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Errore */}
            {error && (
                <div style={{ background: '#ffebee', border: '1px solid #f44336', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c62828', fontSize: 13 }}>
                    ❌ {error}
                </div>
            )}

            {/* Risultati */}
            {data && (
                <>
                    {/* Totali */}
                    <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                        <p style={{ margin: '0 0 12px 0', fontSize: 12, color: '#999' }}>
                            Periodo: <strong>{fmtDate(data.period.from)}</strong> → <strong>{fmtDate(data.period.to)}</strong>
                        </p>
                        <h3 style={{ margin: '0 0 12px 0', fontSize: 14, color: '#555' }}>🌍 Totali complessivi</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                            <Badge color="grey" label="Totale contatti" value={data.totals.grand} />
                            <Badge color="green" label="💬 WA (POI)" value={data.totals.wa} />
                            <Badge color="blue" label="📞 Tel (POI)" value={data.totals.phone} />
                            <Badge color="orange" label="📧 Email (POI)" value={data.totals.email} />
                            <Badge color="green" label="💬 WA (eventi)" value={data.totals.eventWa} />
                            <Badge color="blue" label="📞 Tel (eventi)" value={data.totals.eventPhone} />
                            <Badge color="orange" label="📧 Email (eventi)" value={data.totals.eventEmail} />
                        </div>
                    </div>

                    {/* Tabella POI */}
                    <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                        <Section title="📍 Contatti per POI (Info Aggiuntive + Info OSM)">
                            <Table
                                cols={[
                                    { key: 'osmId', label: 'OSM ID' },
                                    { key: 'wa', label: '💬 WA', render: r => r.wa ? `${r.wa} (${r.waUnique} unici)` : '—' },
                                    { key: 'phone', label: '📞 Tel', render: r => r.phone ? `${r.phone} (${r.phoneUnique} unici)` : '—' },
                                    { key: 'email', label: '📧 Email', render: r => r.email ? `${r.email} (${r.emailUnique} unici)` : '—' },
                                    { key: 'tot', label: 'Totale', render: r => fmt((r.wa || 0) + (r.phone || 0) + (r.email || 0)) },
                                    { key: 'last', label: 'Ultimo click', render: r => fmtDate(r.waLast || r.phoneLast || r.emailLast) },
                                ]}
                                rows={poiMerged}
                            />
                        </Section>
                    </div>

                    {/* Tabella Eventi */}
                    <div style={{ background: 'white', border: '1px solid #e0e0e0', borderRadius: 10, padding: 16 }}>
                        <Section title="✨ Contatti per Evento Attività">
                            <Table
                                cols={[
                                    { key: 'eventId', label: 'Evento ID' },
                                    { key: 'whatsapp', label: '💬 WA', render: r => r.whatsapp ? `${r.whatsapp} (${r.whatsappUnique} unici)` : '—' },
                                    { key: 'phone', label: '📞 Tel', render: r => r.phone ? `${r.phone} (${r.phoneUnique} unici)` : '—' },
                                    { key: 'email', label: '📧 Email', render: r => r.email ? `${r.email} (${r.emailUnique} unici)` : '—' },
                                    { key: 'tot', label: 'Totale', render: r => fmt((r.whatsapp || 0) + (r.phone || 0) + (r.email || 0)) },
                                ]}
                                rows={eventMerged}
                            />
                        </Section>
                    </div>
                </>
            )}
        </div>
    );
}
